import { useState, useEffect } from "react";
import {
  Box,
  Container,
  Paper,
  Tabs,
  Tab,
  Typography,
  Alert,
  Card,
  Grid,
  Chip,
  CircularProgress,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { updateScreenTitle } from "../../../../LayoutContainers/AppLayout/redux/AppSlice";
import WarningIcon from "@mui/icons-material/Warning";
import useWallet from "../../../../hooks/useWallet";
import useChannelPartner from "../../../../hooks/useChannelPartner";
import AppService from "../../../../Configurations/Appservice";
import CustomerTopupVerification from "./tabs/CustomerTopupVerification";
import PartnerPaymentConfirmation from "./tabs/PartnerPaymentConfirmation";
import ChannelCommissionConfirmation from "./tabs/ChannelCommissionConfirmation";

// Utility function to format currency (fallback if not in hook)
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'AED',
    minimumFractionDigits: 2,
  }).format(amount || 0);
};

/**
 * TabPanel Component
 */
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`approval-tabpanel-${index}`}
      aria-labelledby={`approval-tab-${index}`}
      style={{ width: "100%", height: "100%" }}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2, height: "100%" }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `approval-tab-${index}`,
    "aria-controls": `approval-tabpanel-${index}`,
  };
}

/**
 * PendingApprovalsPage - Dedicated page for approval management
 *
 * Three tabs:
 * 1. Customer Top-up Verification (Money IN) - Thaklis Admin can APPROVE
 * 2. Partner Payment Confirmation (Money OUT) - View Only
 * 3. Channel Commission Confirmation (Money OUT) - View Only
 *
 * Access: /wallet/pending-approvals
 */
const PendingApprovalsPage = () => {
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);
  const [summary, setSummary] = useState(null);
  const [urgentCount, setUrgentCount] = useState(0);
  const [servicePartnerPayments, setServicePartnerPayments] = useState([]);
  const [servicePartnerLoading, setServicePartnerLoading] = useState(false);

  // Use hooks for data
  const { getDashboardSummary, loading: walletLoading, error: walletError } = useWallet();
  const {
    fetchPendingPayments,
    fetchPendingCommissions,
    loading: cpLoading,
    error: cpError
  } = useChannelPartner();

  useEffect(() => {
    dispatch(updateScreenTitle("Pending Approvals"));
    return () => dispatch(updateScreenTitle(""));
  }, [dispatch]);

  // Fetch service partner payments for Partner Payment Confirmation tab
  useEffect(() => {
    const fetchServicePartnerPayments = async () => {
      setServicePartnerLoading(true);
      try {
        const response = await AppService.get("v2/partner-payment-transactions/pending");
        console.log("Fetched partner payment transactions:", response);
        setServicePartnerPayments(Array.isArray(response) ? response : []);
      } catch (err) {
        console.error('Failed to fetch partner payment transactions:', err);
        setServicePartnerPayments([]);
      } finally {
        setServicePartnerLoading(false);
      }
    };

    fetchServicePartnerPayments();
  }, []);

  // Fetch pending approvals data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch dashboard summary for pending approvals counts
        const dashboardData = await getDashboardSummary();

        // Fetch pending payments
        await fetchPendingPayments();

        // Fetch pending commissions
        await fetchPendingCommissions();

        // Build summary from fetched data
        const summary = {
          totals: {
            totalAwaitingAction: 0,
            totalAmount: 0,
            urgentActions: 0
          },
          customerTopups: { count: 0, items: [] },
          inTransit: { count: 0, items: [] },
          channelCommissions: { count: 0, items: [] }
        };

        if (dashboardData?.pendingApprovals) {
          summary.totals.totalAwaitingAction = dashboardData.pendingApprovals.count || 0;
          summary.totals.totalAmount = dashboardData.pendingApprovals.amount || 0;
          summary.customerTopups.count = dashboardData.pendingApprovals.topups || 0;
          summary.inTransit.count = dashboardData.pendingApprovals.inTransit || 0;
          summary.channelCommissions.count = dashboardData.pendingApprovals.commissions || 0;
        }

        setSummary(summary);
        setUrgentCount(summary.totals.urgentActions);
      } catch (err) {
        console.error('Failed to fetch pending approvals:', err);
        // Set empty summary on error to show UI
        setSummary({
          totals: { totalAwaitingAction: 0, totalAmount: 0, urgentActions: 0 },
          customerTopups: { count: 0, items: [] },
          inTransit: { count: 0, items: [] },
          channelCommissions: { count: 0, items: [] }
        });
      }
    };

    fetchData();
  }, [getDashboardSummary, fetchPendingPayments, fetchPendingCommissions]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const isLoading = walletLoading || cpLoading;
  const hasError = walletError || cpError;

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!summary) {
    return <Typography>Unable to load pending approvals</Typography>;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Error Alert */}
      {hasError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load pending approvals. Some data may be unavailable. {hasError}
        </Alert>
      )}

      {/* Header Section - Simple */}
      <Paper sx={{ p: 2.5, mb: 3, bgcolor: "primary.main", color: "white" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 0.5 }}>
              Pending Approvals
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.95 }}>
              {summary.totals.totalAwaitingAction} items awaiting action
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography variant="caption" sx={{ opacity: 0.9, display: "block" }}>
              Total Pending
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {formatCurrency(summary.totals.totalAmount)}
            </Typography>
          </Box>
        </Box>
      </Paper>


      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="approval tabs"
          variant="fullWidth"
          textColor="primary"
          indicatorColor="primary"
          sx={{
            "& .MuiTab-root": {
              fontWeight: "bold",
              fontSize: "0.9rem",
              textTransform: "none",
              minHeight: "56px",
            },
          }}
        >
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <span>Customer Top-up Verification</span>
                <Chip
                  label={summary.customerTopups.count}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              </Box>
            }
            {...a11yProps(0)}
          />
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <span>Partner Payment Confirmation</span>
                <Chip
                  label={servicePartnerPayments.length}
                  size="small"
                  color={servicePartnerPayments.length > 0 ? "warning" : "primary"}
                  variant="outlined"
                />
              </Box>
            }
            {...a11yProps(1)}
          />
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <span>Channel Commission Confirmation</span>
                <Chip
                  label={summary.channelCommissions.count}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              </Box>
            }
            {...a11yProps(2)}
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box sx={{ flexGrow: 1 }}>
        <TabPanel value={tabValue} index={0}>
          <CustomerTopupVerification data={summary.customerTopups} />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <PartnerPaymentConfirmation data={servicePartnerPayments} loading={servicePartnerLoading} />
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <ChannelCommissionConfirmation data={summary.channelCommissions} />
        </TabPanel>
      </Box>

    </Container>
  );
};

export default PendingApprovalsPage;
